import React, { useState, useEffect } from 'react'
import {
    StyledTextKeyword,
    StyledSearchFiltersByKeywords,
    StyledSearchFiltersByKeywordsRow,
    StyledSearchFiltersByKeywordsSelectedButton,
    StyledSearchFiltersByKeywordsSelectedContainer,
    StyledSearchFiltersByKeywordsInput,
    StyledButton,
} from './search-filters-by-keywords.styles'
import { useFilter } from '../../forms/hocs/filter.context.hoc'
import { FilterComponentProp } from '../../forms/hocs/types'
import { IconClose } from '@at/design-system-icons'

export const MAX_KEYWORDS = 15
const MAX_LENGTH_KEYWORD = 50

export const SearchFiltersByKeywords = ({ placeholder }: FilterComponentProp) => {
    const { onValueChanged, selected, filterName } = useFilter()
    const [inputText, setInputText] = useState<string>('')
    const [selectedKeywords, setSelectedKeywords] = useState<string[]>([])
    const [canInputError, setCanInputError] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')

    //Split by commas if the input contains comma, remove duplicated, slice by MAX_KEYWORDS and remove empty strings
    const parseKeywords = (keywords: ReadonlyArray<string>) => [
        ...new Set(
            keywords
                .join(',')
                .split(',')
                .map((keyword: string) =>
                    keyword.toLowerCase().replace(/\s+/g, ' ').trim().slice(0, MAX_LENGTH_KEYWORD)
                )
                .filter((keyword: string) => keyword.trim().length)
                .slice(-MAX_KEYWORDS)
        ),
    ]
    const addKeyword = () => {
        const updatedKeywordsFiltered = parseKeywords([inputText, ...selectedKeywords])
        onValueChanged(updatedKeywordsFiltered)
        const hasNoTextInput = inputText.length < 1
        const hasKeywordAlreadyAdded = selectedKeywords.includes(inputText.trim().toLowerCase())
        const hasTooManyKeywords = selectedKeywords.length + 1 > MAX_KEYWORDS
        setCanInputError(hasNoTextInput || hasKeywordAlreadyAdded || hasTooManyKeywords)
        if (hasTooManyKeywords) {
            setErrorMessage('You can only add up to 15 keywords')
        } else if (hasKeywordAlreadyAdded) {
            setErrorMessage('Keyword already added')
        } else if (hasNoTextInput) {
            setErrorMessage('Please enter a keyword')
        } else {
            setErrorMessage('')
        }
        setInputText('')
    }
    const removeKeyword = (indexDelete: number) => {
        const updatedKeywords = selectedKeywords.filter((_, index) => indexDelete !== index)
        setCanInputError(false)
        onValueChanged(updatedKeywords ? updatedKeywords : [])
    }
    useEffect(() => {
        setSelectedKeywords(parseKeywords(selected))
    }, [selected])

    return (
        <StyledSearchFiltersByKeywords data-testid="search-filter-keywords">
            <StyledSearchFiltersByKeywordsRow>
                <StyledSearchFiltersByKeywordsInput
                    id={filterName}
                    name={filterName}
                    error={canInputError}
                    errorText={errorMessage}
                    fieldType="text"
                    label={placeholder || ''}
                    value={inputText}
                    data-testid="filter-by-keyword-input"
                    onChange={(e) => setInputText(e.target.value.replace(/\s+/g, ' '))}
                    placeholder="Add keyword"
                    maxLength={50}
                    onKeyDown={(event) => {
                        setCanInputError(false)
                        if (event.key === 'Enter') {
                            addKeyword()
                        }
                    }}
                />
                <StyledButton displayType="secondary" text="Add" onInteraction={addKeyword}></StyledButton>
            </StyledSearchFiltersByKeywordsRow>
            <StyledSearchFiltersByKeywordsSelectedContainer>
                {selectedKeywords.map((keyword, index) => {
                    return (
                        <StyledSearchFiltersByKeywordsSelectedButton
                            as="button"
                            key={`keywords-${keyword}-${index}`}
                            data-testid={`filter-by-keyword-selected-${index}`}
                            onClick={() => {
                                removeKeyword(index)
                            }}
                        >
                            <StyledTextKeyword as="span">{keyword}</StyledTextKeyword>
                            <IconClose title="Remove keyword" customSize={{ height: 12, width: 12 }} />
                        </StyledSearchFiltersByKeywordsSelectedButton>
                    )
                })}
            </StyledSearchFiltersByKeywordsSelectedContainer>
        </StyledSearchFiltersByKeywords>
    )
}
